/**
 * Main-thread measurement for the document editor: span timers, a long-task
 * watch, memo-miss attribution, a page-raster tally, the open-to-first-page
 * timeline, and the reset registry that clears all of them at each open. Each section carries its own rationale; what they share is that every
 * one of them was added because something had been ARGUED about and got it wrong.
 *
 *
 * The MuPDF queue probe (`mupdfRenderWorkerClient`) only sees the worker, which
 * made the wrong half of the pipeline look guilty. A worker response is only
 * DELIVERED when the main thread is free, and the client's own queue lives on
 * the main thread too — so main-thread congestion inflates the queue probe's
 * `service` times AND delays dispatch, showing up there as if the worker were
 * slow. Shrinking the text pre-pass's range on that reading made things ~5x
 * worse (see PREPASS_RANGE_PAGES): six times the round trips, six times the
 * per-chunk main-thread burst.
 *
 * So the two sides have to be measured together. This records what the main
 * thread actually spends per unit of work; `redactQueueDump()` prints it under
 * the per-tier worker numbers.
 *
 * Recording is unconditional (a `performance.now()` pair and a ring-buffer
 * write) so it works in a production build on someone else's machine, which is
 * the whole reason it exists. Bounded memory: one fixed ring per label.
 *
 * ACTIVATION: `DocumentCanvas` starts the watch on MOUNT and stops it on
 * unmount, so it is scoped to an editor session and RESTARTS on the next one.
 *
 * SCOPE: every buffer here is cleared by `markDocumentOpen()`, so a dump
 * describes ONE document. Before that, counters ran from tab load and a second
 * open in the same session merged two documents into one set of percentiles —
 * which is how the page-raster tally reported a `16p x2` "double raster" that
 * did not exist (see PERFORMANCE.md). Contaminated output looks entirely
 * plausible, so this is a correctness property, not a convenience.
 *
 * Two ways to get this wrong, both already made: starting it on module import
 * here runs it at app boot for every user of the app, because this module is
 * reachable from the ROOT SAGA (`common-app/state/sagas` -> `documents/sagas` ->
 * `sagas/pdfPrepass` -> here) and that is not code-split. And starting it once
 * from `perfProbe`'s module scope while stopping it on unmount means it never
 * comes back for a second document — the watch silently reported itself as an
 * unsupported browser from then on.
 *
 * Spans only see code we thought to wrap. The LONG-TASK watch below covers what
 * they cannot: time the main thread spends blocked BETWEEN tasks — GC over a
 * large heap, React reconciliation, structured-clone deserialization (which
 * happens before any message handler runs, so it is inside the queue probe's
 * `service` but inside no span). On the machine that prompted this, spans plus
 * known MuPDF work accounted for only ~200 ms of a ~1100 ms round trip; the
 * blocked percentage is what tells you where the rest went.
 */

/** Samples kept per label. */
const SPAN_SAMPLE_LIMIT = 1000;

interface SpanAccumulator {
  samples: Float64Array;
  /** Total recorded; > SPAN_SAMPLE_LIMIT means the ring has wrapped. */
  written: number;
  /** Summed over ALL samples, not just the retained ring — total time spent. */
  totalMs: number;
}

const spans = new Map<string, SpanAccumulator>();

/** Record one occurrence of `label` taking `ms`. Cheap enough for a hot path. */
export function recordSpan(label: string, ms: number): void {
  let acc = spans.get(label);
  if (!acc) {
    acc = {
      samples: new Float64Array(SPAN_SAMPLE_LIMIT),
      written: 0,
      totalMs: 0,
    };
    spans.set(label, acc);
  }
  acc.samples[acc.written % SPAN_SAMPLE_LIMIT] = ms;
  acc.written += 1;
  acc.totalMs += ms;
}

/** Time `fn`, record it under `label`, and return its result. */
export function timeSpan<T>(label: string, fn: () => T): T {
  const startedAt = performance.now();
  try {
    return fn();
  } finally {
    recordSpan(label, performance.now() - startedAt);
  }
}

export interface MainThreadSpanStats {
  label: string;
  count: number;
  /** Cumulative main-thread time spent under this label. The headline number. */
  totalMs: number;
  meanMs: number;
  medianMs: number;
  p90Ms: number;
  maxMs: number;
}

/** Snapshot every recorded span, worst cumulative cost first. */
export function mainThreadSpanStats(): MainThreadSpanStats[] {
  const out: MainThreadSpanStats[] = [];
  const round = (x: number): number => Math.round(x * 100) / 100;
  spans.forEach((acc, label) => {
    const n = Math.min(acc.written, SPAN_SAMPLE_LIMIT);
    if (n === 0) {
      return;
    }
    const sorted = Array.from(acc.samples.subarray(0, n)).sort((a, b) => a - b);
    const at = (q: number): number =>
      sorted[Math.min(n - 1, Math.floor(q * n))] ?? 0;
    out.push({
      label,
      count: acc.written,
      totalMs: round(acc.totalMs),
      meanMs: round(acc.totalMs / acc.written),
      medianMs: round(at(0.5)),
      p90Ms: round(at(0.9)),
      maxMs: round(sorted[n - 1] ?? 0),
    });
  });
  return out.sort((a, b) => b.totalMs - a.totalMs);
}

/**
 * Drop every recorded span AND the long-task tally, so a dump after this is
 * internally consistent. Resetting only the spans left `redactQueueDump()`
 * printing spans-since-reset directly above blocked-%-since-page-load, which
 * made "clear the samples to time one interaction" quietly untrue.
 */
export function resetMainThreadSpans(): void {
  spans.clear();
  longTasks.count = 0;
  longTasks.totalMs = 0;
  longTasks.maxMs = 0;
  longTasks.since = performance.now();
}

/**
 * Stop watching long tasks (document editor unmounted). Leaves the recorded
 * totals in place so a dump afterwards still reports the session.
 */
export function stopLongTaskWatch(): void {
  longTaskObserver?.disconnect();
  longTaskObserver = null;
}

// ---------------------------------------------------------------------------
// Long-task watch
//
// `longtask` entries are main-thread tasks >= 50 ms — i.e. the ones that stall
// a worker response's delivery, delay the client's next dispatch, and drop
// frames. Reported as a PERCENTAGE OF WALL CLOCK, which is the number that says
// whether the main thread is the bottleneck at all.
// ---------------------------------------------------------------------------

interface LongTaskTotals {
  count: number;
  totalMs: number;
  maxMs: number;
  since: number;
}

const longTasks: LongTaskTotals = {
  count: 0,
  totalMs: 0,
  maxMs: 0,
  since: typeof performance !== 'undefined' ? performance.now() : 0,
};

let longTaskObserver: PerformanceObserver | null = null;
/**
 * Whether the `longtask` entry type works here at all: null until we've tried.
 * Kept separate from `longTaskObserver` because that goes null on every stop,
 * and conflating the two made a STOPPED watch report itself as an UNSUPPORTED
 * browser — which is exactly what it looked like in the field after the editor
 * had been closed once.
 */
let longTaskSupported: boolean | null = null;

/**
 * Begin watching long tasks, and start the measurement window. Idempotent, and a
 * no-op where the entry type isn't supported (Safari, Firefox) — those browsers
 * report `supported: false` rather than throwing.
 *
 * NOT `buffered: true`, deliberately. Buffered entries predate `since`, so they
 * were being folded into a window that starts here — bundle parse, auth and the
 * dashboard's own long tasks inflated `blockedPercent`, which could then exceed
 * 100% and varied with how long the user had been in the app before opening a
 * document. An uncontrolled measurement is worse than a slightly late one.
 */
export function startLongTaskWatch(): void {
  if (longTaskObserver || typeof PerformanceObserver === 'undefined') {
    return;
  }
  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        longTasks.count += 1;
        longTasks.totalMs += entry.duration;
        if (entry.duration > longTasks.maxMs) {
          longTasks.maxMs = entry.duration;
        }
      }
    });
    observer.observe({ type: 'longtask' });
    longTaskObserver = observer;
    longTaskSupported = true;
    longTasks.since = performance.now();
  } catch {
    longTaskSupported = false;
  }
}

export interface LongTaskStats {
  /** The browser supports `longtask` (false on Safari/Firefox/jsdom). */
  supported: boolean;
  /** A watch is running right now. False between editor sessions. */
  watching: boolean;
  count: number;
  totalMs: number;
  maxMs: number;
  /** Wall clock the watch has covered. */
  windowMs: number;
  /** Share of that window spent in tasks >= 50 ms. The headline. */
  blockedPercent: number;
}

export function longTaskStats(): LongTaskStats {
  const windowMs = performance.now() - longTasks.since;
  const round = (x: number): number => Math.round(x * 10) / 10;
  return {
    supported: longTaskSupported === true,
    watching: longTaskObserver !== null,
    count: longTasks.count,
    totalMs: round(longTasks.totalMs),
    maxMs: round(longTasks.maxMs),
    windowMs: round(windowMs),
    blockedPercent:
      windowMs > 0 ? round((longTasks.totalMs / windowMs) * 100) : 0,
  };
}

// ---------------------------------------------------------------------------
// Memo-miss attribution
//
// `memo` only helps when EVERY prop holds its identity, so one unstable prop
// among forty-five silently makes the wrapper free of charge — and there is no
// way to tell from the outside which one it was. This counts, per component and
// per prop name, how often that prop was the (or a) reason a memoized render was
// not skipped.
//
// Read it to decide what to fix, in frequency order. The alternative is
// reasoning about which props "should" be stable, which is how DocumentPage came
// to be memoized while 29 of its 33 handlers arrived as fresh inline arrows.
// ---------------------------------------------------------------------------

/** component -> prop name -> times that prop differed on a memo compare. */
const memoMisses = new Map<string, Map<string, number>>();
/** component -> compares, and how many of those skipped the render. */
const memoCompares = new Map<string, { total: number; skipped: number }>();

export function recordMemoCompare(component: string, skipped: boolean): void {
  const c = memoCompares.get(component) ?? { total: 0, skipped: 0 };
  c.total += 1;
  if (skipped) {
    c.skipped += 1;
  }
  memoCompares.set(component, c);
}

export function recordMemoMiss(component: string, prop: string): void {
  let byProp = memoMisses.get(component);
  if (!byProp) {
    byProp = new Map<string, number>();
    memoMisses.set(component, byProp);
  }
  byProp.set(prop, (byProp.get(prop) ?? 0) + 1);
}

export interface MemoStats {
  component: string;
  compares: number;
  /** Renders actually avoided. If this is ~0 the memo is doing nothing. */
  skipped: number;
  /** Prop names that differed, most frequent first. */
  offenders: Array<{ prop: string; count: number }>;
}

export function memoStats(): MemoStats[] {
  const out: MemoStats[] = [];
  memoCompares.forEach((c, component) => {
    const byProp = memoMisses.get(component);
    const offenders = byProp
      ? Array.from(byProp, ([prop, count]) => ({ prop, count })).sort(
          (a, b) => b.count - a.count
        )
      : [];
    out.push({
      component,
      compares: c.total,
      skipped: c.skipped,
      offenders,
    });
  });
  return out.sort((a, b) => b.compares - a.compares);
}

export function resetMemoStats(): void {
  memoMisses.clear();
  memoCompares.clear();
}

/**
 * Drop-in replacement for `memo`'s default comparator that additionally reports
 * WHY it returned false.
 *
 * Semantics match React's default shallow compare exactly, which matters more
 * than it sounds: this now stands in front of every redaction and detection
 * box, so a divergence would change what renders rather than merely what is
 * observed. Two details are deliberate, and both were wrong once (VE-30193):
 *
 *   - `Object.is`, not `!==`. They differ on `NaN` (and on `±0`), and a `NaN`
 *     prop is reachable here — `scale` is derived from a measured width that is
 *     momentarily zero during layout. Under `!==` such a prop compares unequal
 *     forever, so the memoized component re-renders where stock `memo` would
 *     skip: the opposite of the point.
 *   - the key loop checks membership in `prev`, not just equality. Comparing
 *     key COUNTS and then iterating only `nextKeys` calls a render equal when
 *     one prop is dropped and another added as `undefined` — counts match,
 *     every `next` key reads `undefined` on both sides. Unreachable from a
 *     fixed-prop call site, reachable the moment one spreads conditionally.
 */
export function shallowEqualWithMemoStats<P extends object>(
  component: string
): (prev: Readonly<P>, next: Readonly<P>) => boolean {
  return (prev, next) => {
    const prevKeys = Object.keys(prev);
    const nextKeys = Object.keys(next);
    let equal = prevKeys.length === nextKeys.length;
    for (const key of nextKeys) {
      if (
        !Object.prototype.hasOwnProperty.call(prev, key) ||
        !Object.is(
          (prev as Record<string, unknown>)[key],
          (next as Record<string, unknown>)[key]
        )
      ) {
        equal = false;
        recordMemoMiss(component, key);
      }
    }
    recordMemoCompare(component, equal);
    return equal;
  };
}

// ---------------------------------------------------------------------------
// Page-raster request tally
//
// How many times each page's MuPDF raster was requested. A page should need ONE
// per (page, width) — more means a dependency of the request effect is settling
// after mount and the earlier rasters were thrown away, which on a single-slot
// worker puts wasted renders in front of the page the user is waiting for.
//
// This exists because that was ASSUMED once and the assumption was wrong: a
// container-width fix predicted the request count would halve, and it did not
// move at all (33 -> 33). Count it, don't reason about it.
// ---------------------------------------------------------------------------

const pageRasterRequests = new Map<number, number>();

export function recordPageRasterRequest(page: number): void {
  pageRasterRequests.set(page, (pageRasterRequests.get(page) ?? 0) + 1);
}

export interface PageRasterStats {
  /** Total requests issued. */
  total: number;
  /** How many distinct pages were asked for. */
  distinctPages: number;
  /** requests-per-page -> how many pages had exactly that many. */
  histogram: Array<{ times: number; pages: number }>;
  /** The most re-requested pages. */
  worst: Array<{ page: number; times: number }>;
}

export function pageRasterStats(): PageRasterStats {
  const byCount = new Map<number, number>();
  let total = 0;
  pageRasterRequests.forEach((times) => {
    total += times;
    byCount.set(times, (byCount.get(times) ?? 0) + 1);
  });
  return {
    total,
    distinctPages: pageRasterRequests.size,
    histogram: Array.from(byCount, ([times, pages]) => ({ times, pages })).sort(
      (a, b) => a.times - b.times
    ),
    worst: Array.from(pageRasterRequests, ([page, times]) => ({ page, times }))
      .sort((a, b) => b.times - a.times)
      .slice(0, 5),
  };
}

export function resetPageRasterStats(): void {
  pageRasterRequests.clear();
}

// ---------------------------------------------------------------------------
// Open-to-first-page timeline
//
// "The first page takes 3-5s" is a CHAIN, and aggregates cannot decompose it.
// Worse, they mislead: `HIGH wait`'s median is the middle of a ~16-deep burst of
// page rasters, while the page the user is waiting for sits at the FRONT of that
// queue — so the median was being read as time-to-first-page when it is nothing
// of the kind.
//
// A page cannot raster until `nativeWidth > 0`, which arrives from the dims
// pre-pass or pdf.js's per-page load; each link in the chain is a hop through a
// main thread that is ~60% blocked. Milestones, timed from document open, say
// which link actually costs the seconds.
// ---------------------------------------------------------------------------

let openStartedAt: number | null = null;
const milestones = new Map<string, number>();

/**
 * Sample buffers owned by OTHER modules, to be cleared alongside this one's
 * whenever a document opens.
 *
 * A registration rather than a direct import on purpose. The only caller of
 * `markDocumentOpen` is the openDocument saga, which lives in the main bundle;
 * the MuPDF queue counters live in `mupdfRenderWorkerClient`, which is in the
 * lazily-loaded editor chunk and carries a webpack `new Worker(new URL(...))`
 * reference. Importing that from the saga to reach its reset would drag the
 * worker client into the main bundle — a real cost, to save a console command.
 * Inverting it costs nothing: the client registers when it loads, and if it
 * has not loaded yet its counters are empty and there is nothing to clear.
 */
const sampleResets = new Set<() => void>();

/** Returns an unregister function, so a caller can drop out cleanly. */
export function registerSampleReset(fn: () => void): () => void {
  sampleResets.add(fn);
  return () => {
    sampleResets.delete(fn);
  };
}

/**
 * Clear every perf sample buffer — this module's and every registered one.
 * The single definition of "reset", shared by `redactQueueReset()` and the
 * automatic reset at document open.
 */
export function resetAllSamples(): void {
  resetMainThreadSpans();
  resetMemoStats();
  resetPageRasterStats();
  for (const reset of sampleResets) {
    reset();
  }
}

/**
 * Start (or restart) the timeline. Called when a document begins opening.
 *
 * This also CLEARS every sample buffer, so a dump always describes the document
 * currently open rather than everything since the tab loaded. Before this, a
 * second open in the same session silently mixed two documents' samples into
 * one set of percentiles unless `redactQueueReset()` was typed by hand first —
 * and forgetting was easy, because the output looks perfectly plausible either
 * way. `resetMainThreadSpans` also restarts the long-task window, so
 * `blockedPercent` is measured from this open too.
 */
export function markDocumentOpen(): void {
  resetAllSamples();
  openStartedAt = performance.now();
  milestones.clear();
}

/**
 * Record a milestone, once. Later occurrences are ignored — we want the FIRST
 * page painted, not the sixteenth. No-op before `markDocumentOpen`.
 */
export function recordMilestone(name: string): void {
  if (openStartedAt === null || milestones.has(name)) {
    return;
  }
  milestones.set(name, performance.now() - openStartedAt);
}

export interface OpenTimeline {
  started: boolean;
  /** Milestone name -> ms since document open, in chronological order. */
  marks: Array<{ name: string; atMs: number }>;
}

export function openTimeline(): OpenTimeline {
  return {
    started: openStartedAt !== null,
    marks: Array.from(milestones, ([name, atMs]) => ({
      name,
      atMs: Math.round(atMs),
    })).sort((a, b) => a.atMs - b.atMs),
  };
}
